import React, { useState } from "react";
import {
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Box,
} from "@mui/material";
import { Iconography, IconographySizes } from "@hid-galaxy-ui/galaxy-react";
import { useAppDispatch } from "../../../../../../../redux";
import { partnerDetailsSliceAction } from "../../../../../../../redux/slices/partner-details";

export type ReportFilter = {
  field: string;
  operator: "=" | "!=" | ">" | "<" | "IN" | "NOT IN";
  value: string | string[];
};

type FilterTabProps = {
  selectedColumns: { value: string; label: string }[];
  appliedFilters: ReportFilter[];
  onApplyFilters: (filters: ReportFilter[]) => void;
  onResetFilters: () => void;
  readOnly?: boolean;
};

const operators = [
  { label: "Equals", value: "=" },
  { label: "Not Equal To", value: "!=" },
  { label: "Greater Than", value: ">" },
  { label: "Less Than", value: "<" },
  { label: "IN", value: "IN" },
  { label: "NOT IN", value: "NOT IN" },
];

const FilterTab: React.FC<FilterTabProps> = ({
  selectedColumns,
  appliedFilters,
  onApplyFilters,
  onResetFilters,
  readOnly = false,
}) => {
  const [field, setField] = useState("");
  const [operator, setOperator] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [values, setValues] = useState<string[]>([]);

  const dispatch = useAppDispatch();

  const clearDraft = () => {
    setField("");
    setOperator("");
    setInputValue("");
    setValues([]);
  };

  const getOperatorLabel = (value: string) =>
    operators.find((op) => op.value === value)?.label || value;

  const getFieldLabel = (fieldValue: string) =>
    selectedColumns.find((col) => col.value === fieldValue)?.label || fieldValue;

  const formatFilterValue = (filter: ReportFilter) => {
    const operatorLabel = getOperatorLabel(filter.operator);
    const valueText = Array.isArray(filter.value)
      ? filter.value.join(", ")
      : filter.value;
    return `${operatorLabel} : ${valueText}`;
  };

  const handleApplyFilter = () => {
    const finalValue =
      operator === "IN" || operator === "NOT IN" ? values : inputValue.trim();

    const newFilter: ReportFilter = {
      field,
      operator: operator as ReportFilter["operator"],
      value: finalValue,
    };

    const updatedFilters = [...appliedFilters, newFilter];

    dispatch(partnerDetailsSliceAction.setReportFilterAction(updatedFilters));
    onApplyFilters(updatedFilters);
    clearDraft();
  };

  const handleDeleteFilter = (index: number) => {
    const updatedFilters = appliedFilters.filter((_, i) => i !== index);

    dispatch(partnerDetailsSliceAction.setReportFilterAction(updatedFilters));
    onApplyFilters(updatedFilters);
  };

  const handleFilterResetClick = () => {
    clearDraft();
    dispatch(partnerDetailsSliceAction.setReportFilterAction([]));
    onResetFilters();
  };

  const handleAddValue = () => {
    const trimmed = inputValue.trim();
    if (!trimmed || values.includes(trimmed)) return;

    setValues((prev) => [...prev, trimmed]);
    setInputValue("");
  };

  const handleDeleteValue = (val: string) => {
    setValues((prev) => prev.filter((v) => v !== val));
  };

  const isApplyDisabled =
    !field ||
    !operator ||
    ((operator === "IN" || operator === "NOT IN")
      ? values.length === 0
      : !inputValue.trim());

  return (
    <div className="rd-filter-panel">
      <div className="rd-filter-section" style={{ marginBottom: 15 }}>
        <InputLabel htmlFor="report-title-input" className="label-inline">
          Filter By
          <span className="rd-filter-count-badge">{appliedFilters.length}</span>
        </InputLabel>
        <strong>Field</strong>
        <FormControl
          fullWidth
          size="small"
          variant="outlined"
          disabled={!selectedColumns.length || readOnly}
        >
          <Select
            value={field}
            label="Field"
            onChange={(e) => setField(e.target.value)}
            displayEmpty
            renderValue={(selected) => {
              if (!selected) return <span>Field</span>;
              const label = selectedColumns.find((c) => c.value === selected)?.label;
              return label || (selected as string);
            }}
          >
            <MenuItem value="">
              <span>Field</span>
            </MenuItem>
            {selectedColumns.map((col) => (
              <MenuItem key={col.value} value={col.value}>
                {col.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <div className="rd-filter-section" style={{ marginBottom: 15 }}>
        <strong>Operator</strong>
        <FormControl
          fullWidth
          size="small"
          variant="outlined"
          disabled={!field || readOnly}
        >
          <Select
            value={operator}
            label="Operator"
            onChange={(e) => setOperator(e.target.value)}
            displayEmpty
            renderValue={(selected) =>
              selected ? getOperatorLabel(selected as string) : <span>Operator</span>
            }
          >
            <MenuItem value="">
              <span>Operator</span>
            </MenuItem>
            {operators.map((op) => (
              <MenuItem key={op.value} value={op.value}>
                {op.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <div className="rd-filter-section" style={{ marginBottom: 24 }}>
        <strong>Use Relative Value</strong>

        {operator === "IN" || operator === "NOT IN" ? (
          <>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder="Type value and press Enter"
              value={inputValue}
              disabled={!operator || readOnly}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddValue();
                }
              }}
            />

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
              {values.map((val) => (
                <Box
                  key={val}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    background: "#E0E0E0",
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2">{val}</Typography>
                  {!readOnly && (
                    <Button
                      size="small"
                      onClick={() => handleDeleteValue(val)}
                      sx={{ minWidth: 24, ml: 0.5 }}
                    >
                      ×
                    </Button>
                  )}
                </Box>
              ))}
            </Box>
          </>
        ) : (
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Use Relative Value"
            value={inputValue}
            disabled={!operator || readOnly}
            onChange={(e) => setInputValue(e.target.value)}
          />
        )}
      </div>

      {!readOnly && (
        <div className="rd-filter-buttons">
          <Button
            variant="text"
            color="primary"
            onClick={handleFilterResetClick}
            className="rd-filter-button-reset"
          >
            Reset
          </Button>
          <Button
            variant="contained"
            className="rd-filter-button-apply"
            disabled={isApplyDisabled}
            onClick={handleApplyFilter}
          >
            Apply
          </Button>
        </div>
      )}

      {appliedFilters.length > 0 && (
        <div className="rd-applied-filters">
          {appliedFilters.map((filter, index) => (
            <div key={`${filter.field}-${index}`} className="rd-applied-filter-card">
              <div className="rd-applied-filter-card__content">
                <span className="rd-applied-filter-card__field">
                  {getFieldLabel(filter.field)}
                </span>
                <span className="rd-applied-filter-card__condition">
                  {formatFilterValue(filter)}
                </span>
              </div>
              {!readOnly && (
                <button
                  type="button"
                  className="rd-applied-filter-card__delete"
                  aria-label={`Remove filter on ${getFieldLabel(filter.field)}`}
                  onClick={() => handleDeleteFilter(index)}
                >
                  <Iconography
                    icon="trashCan"
                    size={IconographySizes.Small}
                    style={{ color: "#0057B7", width: "18px", height: "18px" }}
                  />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FilterTab;
